import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  makeStyles,
  Menu,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { Pagination } from "@material-ui/lab";

import FolderImage from "images/folderIcon.svg";
import UploadFolder from "./UploadFolder";
import UploadFile from "./UploadFile";
import { formatDate } from "utils";
import { safeDateFormat } from "../utils";
import {
  DownloadSelectedFiles,
  UploadFolders,
  getFolder,
  getDocOcrableFilesList,
  FILES_PAGE_SIZE,
} from "./FileApi";
import FolderView from "./FolderView";
import FileView from "./FileView";
import { postActivity } from "slices/activitySlice";
import { useDispatch } from "react-redux";
import { Add, Close } from "@material-ui/icons";
import { useSnackbar } from "contexts/SnackbarContext";
import EditPermissions from "./EditPermissions";
import PaginateWorkspace from "./PaginateWorkspace";
import OcrWorkspace from "./OcrWorkspace";
import SearchBar from "./SearchBar";
import BundleIndex from "./BundleIndex";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: "2rem",
  },
  heading: {
    fontSize: "1.5rem",
    fontWeight: "700",
  },
  // Shown on a closed case, whose files are view/download only.
  readOnlyNotice: {
    marginTop: "1rem",
    padding: "0.6rem 1rem",
    borderRadius: 6,
    border: "1px solid #F5C2C0",
    background: "#FDECEA",
    color: "#B71C1C",
    fontSize: "0.9rem",
  },
  uploadbtn: {
    background: "white",
    // padding: "1rem 2rem",
    // cursor: "pointer",
  },
  uploadbtntext: {
    fontSize: "1.125rem",
    fontWeight: "400",
    textTransform: "none",
  },
  menuwidth: {
    width: "10.5rem",
    [theme.breakpoints.down("sm")]: {
      width: "8.375rem",
    },
    [theme.breakpoints.down("xs")]: {
      width: "7.125rem",
      fontSize: "0.875rem",
    },
  },
  // The folders/files table. Cell rules are pinned on the wrapper because
  // Analytics.css ships unscoped table/th/td rules that would restyle it.
  filesTable: {
    width: "100%",
    boxSizing: "border-box",
    overflowX: "auto",
    background: "white",
    border: "1px solid #E5E5E5",
    borderRadius: "6px",
    "& table": {
      width: "100%",
      // Analytics.css caps every table at 80% and gives it a height,
      // margin-top and line-height of its own.
      maxWidth: "100%",
      minWidth: 640,
      height: "auto",
      margin: 0,
      lineHeight: "normal",
      borderCollapse: "collapse",
      // Fixed layout keeps a long name from squeezing the other columns.
      tableLayout: "fixed",
      fontFamily: "inherit",
    },
    "& th, & td": {
      border: "none",
      borderBottom: "1px solid #F0F0F0",
      padding: "0.6rem 1rem",
      textAlign: "left",
      verticalAlign: "middle",
      color: "inherit",
      fontSize: "inherit",
      [theme.breakpoints.down("xs")]: {
        padding: "0.3rem 0.5rem",
      },
    },
    "& thead th": {
      background: "#4a3bff0d",
      borderBottom: "1px solid #E5E5E5",
    },
    // The wrapper's border closes the table; no double line under the last row.
    "& tbody tr:last-child td": {
      borderBottom: "none",
    },
  },
  filesHover: {
    cursor: "pointer",
    background: "white",
    "&:hover": {
      background: "#E0E0E0",
      "& $visibilityHidden": {
        visibility: "visible",
      },
      "& $fileFolderName": {
        textDecoration: "underline",
      },
    },
  },
  filesandfolder: {
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "400",
    padding: "0.6rem 1.5rem",
    "&:hover": {
      background: "#E0E0E0",
    },
  },
  tableHeaderContent: {
    fontWeight: "600",
    fontSize: "1rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.75rem",
    },
  },
  tableRowContent: {
    fontSize: "1rem",
    wordBreak: "break-all",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.75rem",
    },
  },

  visibility: {
    visibility: "visible",
  },
  no_selection: {
    "user-select": "none",
    "-webkit-user-select": "none",
    "-moz-user-select": "none",
    "-khtml-user-select": "none",
    "-ms-user-select": "none",
  },
  fileFolderName: {
    wordBreak: "break-all",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  visibilityHidden: {
    visibility: "hidden",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "1rem",
    background: "white",
  },
}));

export default function Files({
  disputeId,
  folder_id,
  allFiles,
  allFolders,
  filesCount = 0,
  foldersCount = 0,
  page = 1,
  onPageChange,
  reload,
  allProfiles,
  profile,
  parent_folder,
  isHidePermissionsFeature = false,
  workspaceId,
  customDisputeId,
  // Closed case: view and download only.
  readOnly = false,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [buttonsEnabled, setButtonsEnabled] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [filteredFolders, setFilteredFolders] = useState([]);
  const [paginationStatus, setPaginationStatus] = useState(0);
  const [firstLevelFolder, setFirstLevelFolder] = useState(null);
  const [ocrData, setOcrData] = useState(null);
  // Folders and files are paginated server side: a page carries up to
  // FILES_PAGE_SIZE folder rows and FILES_PAGE_SIZE file rows.
  const totalPages = Math.max(
    Math.ceil(foldersCount / FILES_PAGE_SIZE),
    Math.ceil(filesCount / FILES_PAGE_SIZE),
    1
  );
  const isFolderEmpty = filesCount === 0 && foldersCount === 0;

  useEffect(() => {
    // Rows may have been deleted while sitting on the last page
    if (page > totalPages && onPageChange) onPageChange(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    // Only the current page's rows are loaded, so a selection cannot span pages
    setSelectedFiles([]);
    setSelectedFolders([]);
  }, [page]);

  const getFirstLevelFolderId = (folder) => {
    if (!folder) return null;
    let current = folder;
    while (
      current.parent_folder &&
      current.parent_folder.name !== "root" &&
      current.parent_folder.nameToShowUser !== "root"
    ) {
      current = current.parent_folder;
    }
    return current.id;
  };
  const firstLevelFolderId = getFirstLevelFolderId(parent_folder);

  const fetchWorkspaceDetails = () => {
    if (workspaceId && firstLevelFolderId) {
      getFolder(firstLevelFolderId)
        .then((res) => {
          const folderData = res.data?.[0];
          if (folderData) {
            setPaginationStatus(folderData.pagination_status || 0);
            setFirstLevelFolder(folderData);
          }
        })
        .catch((err) => {
          console.error("Error fetching first-level folder details in Files:", err);
        });

      getDocOcrableFilesList(workspaceId, firstLevelFolderId)
        .then((res) => {
          setOcrData(res.data);
        })
        .catch((err) => {
          console.error("Error fetching OCR status in Files:", err);
        });
    }
  };

  useEffect(() => {
    fetchWorkspaceDetails();

    return () => {
      setPaginationStatus(0);
      setOcrData(null);
    };
  }, [workspaceId, firstLevelFolderId]);

  const handleFullReload = () => {
    if (reload) reload();
    fetchWorkspaceDetails();
  };

  useEffect(() => {
    const localFilteredFiles = allFiles.filter(file => selectedFiles.includes(`${file.id}`));
    setFilteredFiles(localFilteredFiles)

  }, [selectedFiles, allFiles])

  useEffect(() => {
    const localFilteredFolders = allFolders.filter(folder => selectedFolders.includes(`${folder.id}`));
    setFilteredFolders(localFilteredFolders)
  }, [selectedFolders, allFolders])

  useEffect(() => {
    const isAllowedUser =
      Array.isArray(parent_folder?.webnyay_allowed_users) &&
      parent_folder.webnyay_allowed_users.some(
        (u) => parseInt(u?.id || u) === parseInt(profile?.id) || parseInt(u?.id || u) === parseInt(profile?.user?.id)
      );
    if (
      parent_folder &&
      parent_folder?.uploaded_by?.id != profile?.user?.id &&
      parent_folder?.webnyay_owner != profile?.id &&
      parent_folder?.webnyay_owner?.id != profile?.id &&
      parent_folder?.edit_permission?.indexOf(profile?.user?.id) == -1 &&
      !isAllowedUser
    ) {
      setButtonsEnabled(false);
    }
    if (
      profile?.profile_type == "sub_admin_tiac_rv" ||
      profile?.profile_type == "case_manager" ||
      profile?.profile_type == "admin"
    ) {
      setButtonsEnabled(true);
    }
  }, [parent_folder]);

  const allFolderIds = allFolders?.map((folder) => `${folder.id}`) || [];
  const allFileIds = allFiles?.map((file) => `${file.id}`) || [];
  const selectedCount = selectedFiles.length + selectedFolders.length;
  const totalCount = allFolderIds.length + allFileIds.length;
  const isAllSelected = totalCount > 0 && selectedCount === totalCount;
  const isAnySelected = selectedCount > 0;
  // Case managers get an extra "Modified on" column.
  const isCaseManager = profile.profile_type === "case_manager";

  const handleSelectAllChange = (event) => {
    if (event.target.checked) {
      setSelectedFiles(allFileIds);
      setSelectedFolders(allFolderIds);
    } else {
      setSelectedFiles([]);
      setSelectedFolders([]);
    }
  };

  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [folderName, setFolderName] = useState("New Folder");

  const handleFolderNameChange = (e) => {
    setFolderName(e.target.value);
  };
  const inputRef = useRef(null);

  const handleOnBlur = (e) => {
    if (folderName == "New Folder") {
      setShowNewFolderInput(false);
      setFolderName("New Folder");
    } else {
      UploadFolders(
        disputeId,
        folderName,
        folder_id,
        parent_folder ? parent_folder?.view_permission : [],
        parent_folder ? parent_folder?.edit_permission : [],
        profile?.id,
        customDisputeId
      ).then((result) => {
        dispatch(
          postActivity(
            "uploaded a folder",
            0,
            false,
            folderName,
            disputeId,
            parent_folder ? parent_folder?.view_permission : [],
            null,
            result?.data?.id
          )
        );
        setShowNewFolderInput(false);
        setFolderName("New Folder");
        handleFullReload();
      });
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    if (buttonsEnabled) setAnchorEl(event.currentTarget);
    else showSnackbar("You don't have edit permissions.", "warning");
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const [anchorElNew, setAnchorElNew] = useState(null);
  const handleClickNew = (event) => {
    if (buttonsEnabled) setAnchorElNew(event.currentTarget);
    else showSnackbar("You don't have edit permissions.", "warning");
  };
  const handleCloseNew = () => {
    setAnchorElNew(null);
  };
  const createNewFolder = () => {
    handleCloseNew();
    setShowNewFolderInput(true);
    setTimeout(() => {
      inputRef.current.focus();
      inputRef.current.select();
    }, 100);
  };
  const [openmodalUploadFolder, setOpenmodalUploadFolder] = useState(false);
  const handleOpenUploadFolderModal = () => {
    setOpenmodalUploadFolder(true);
  };
  const handleCloseUploadFolderModalAndRefresh = () => {
    handleFullReload();
    setOpenmodalUploadFolder(false);
    setAnchorEl(null);
  };
  const handleCloseUploadFolderModal = () => {
    setOpenmodalUploadFolder(false);
    setAnchorEl(null);
  };
  const [openmodalUploadFile, setOpenmodalUploadFile] = useState(false);
  const handleOpenUploadFileModal = () => {
    setOpenmodalUploadFile(true);
  };
  const handleCloseUploadFileModalAndRefresh = () => {
    handleFullReload();
    setOpenmodalUploadFile(false);
    setAnchorEl(null);
  };
  const handleCloseUploadFileModal = () => {
    setOpenmodalUploadFile(false);
    setAnchorEl(null);
  };

  const [openmodal, setOpenmodal] = useState(false);
  const handleOpenEdit = () => {
    if (
      // profile.user.id == file.uploaded_by.id ||
      profile?.profile_type === "case_manager" ||
      profile?.profile_type === "sub_admin_tiac_rv"
    ) {
      setOpenmodal(true);
    } else showSnackbar("Only the owner can edit permissions.", "warning");
  };
  const handleCloseModalAndRefresh = () => {
    handleFullReload();
    setOpenmodal(false);
    setAnchorEl(null);
  };
  const handleCloseModal = () => {
    setOpenmodal(false);
    setAnchorEl(null);
  };

  const handleChangeOnFileCheckbox = (event) => {
    event.stopPropagation();
    if (event.target.checked) {
      selectedFiles.push(event.target.id);
      setSelectedFiles([...selectedFiles]);
    } else {
      setSelectedFiles(() => {
        return selectedFiles.filter((x) => {
          return x !== event.target.id;
        });
      });
    }
  };

  const handleChangeOnFolderCheckbox = (event) => {
    event.stopPropagation();
    if (event.target.checked) {
      selectedFolders.push(event.target.id);
      setSelectedFolders([...selectedFolders]);
    } else {
      setSelectedFolders(() => {
        return selectedFolders.filter((x) => {
          return x !== event.target.id;
        });
      });
    }
  };

  const unSelectAll = () => {
    setSelectedFiles([]);
    setSelectedFolders([]);
  };

  const handleDownloadMultipleFiles = () => {
    const data = {
      folder_ids: selectedFolders,
      file_ids: selectedFiles,
      workspace_id: workspaceId,
    };
    DownloadSelectedFiles(data)
      .then((res) => {
        const urls = res?.data?.urls;
        if (urls && urls.length > 0) {
          urls.forEach((url) => {
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = url;
            document.body.appendChild(iframe);
          });
          showSnackbar("Download initiated...", "info");
          unSelectAll();
        } else {
          showSnackbar("Files not found!", "warning");
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const isFirstLevelFolderLoaded =
    !!firstLevelFolder &&
    !!firstLevelFolderId &&
    String(firstLevelFolder.id) === String(firstLevelFolderId);

  // Files and folders may be added at any depth, including after pagination has
  // completed. Only a run that is currently in progress blocks uploads.
  const isStructuralChangeBlocked = paginationStatus === 1;

  const isFirstLevelFolderOwner =
    isFirstLevelFolderLoaded &&
    (parseInt(firstLevelFolder?.uploaded_by?.id || firstLevelFolder?.uploaded_by) === parseInt(profile?.user?.id) ||
      (firstLevelFolder?.webnyay_owner &&
        (parseInt(firstLevelFolder?.webnyay_owner?.id || firstLevelFolder?.webnyay_owner) === parseInt(profile?.id) ||
          parseInt(firstLevelFolder?.webnyay_owner?.id || firstLevelFolder?.webnyay_owner) === parseInt(profile?.user?.id))) ||
      (Array.isArray(firstLevelFolder?.webnyay_allowed_users) &&
        firstLevelFolder.webnyay_allowed_users.some(
          (u) => parseInt(u?.id || u) === parseInt(profile?.id) || parseInt(u?.id || u) === parseInt(profile?.user?.id)
        )));

  return (
    <Box className={classes.root}>
      <Grid container justify="space-between" alignItems="center" spacing={2}>
        <Grid item>
          <Typography className={classes.heading}>Files</Typography>
        </Grid>
        <Grid item xs={12} sm={8} md={6} style={{ display: "flex", justifyContent: "flex-end" }}>
          <SearchBar workspaceId={workspaceId} />
        </Grid>
      </Grid>
      {readOnly && (
        <Typography className={classes.readOnlyNotice}>
          This case is closed. Files can be viewed and downloaded but not changed.
        </Typography>
      )}
      <Box height="2rem" />
      <Grid container spacing={2} alignItems="center">
        {selectedFiles.length === 0 && selectedFolders.length === 0 ? (
          <>
            {!readOnly && parent_folder && !isStructuralChangeBlocked && isFirstLevelFolderOwner && (
              <>
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    aria-controls="upload-menu"
                    aria-haspopup="true"
                    onClick={handleClick}
                    className={`${classes.menuwidth}`}
                    startIcon={<Add />}
                  >
                    Upload
                    {/* <Typography className={classes.uploadbtntext}>Upload</Typography> */}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    color="primary"
                    aria-controls="new-folder"
                    aria-haspopup="true"
                    onClick={handleClickNew}
                    className={`${classes.uploadbtn} ${classes.menuwidth}`}
                    startIcon={<Add />}
                  >
                    New
                    {/* <Typography className={classes.uploadbtntext}>New</Typography> */}
                  </Button>
                </Grid>
              </>
            )}
            <Grid item xs></Grid>
            {!readOnly && parent_folder && isFirstLevelFolderOwner && (
              <>
                <Grid item>
                  <PaginateWorkspace
                    workspaceId={workspaceId}
                    firstLevelFolderId={firstLevelFolderId}
                    reload={handleFullReload}
                    paginationStatus={paginationStatus}
                    setPaginationStatus={setPaginationStatus}
                    paginationType={4}
                    isEmpty={isFolderEmpty}
                    ocrStatus={ocrData?.status}
                  />
                </Grid>
                <Grid item>
                  <OcrWorkspace
                    workspaceId={workspaceId}
                    firstLevelFolderId={firstLevelFolderId}
                    reload={handleFullReload}
                    paginationStatus={paginationStatus}
                    isEmpty={isFolderEmpty}
                    ocrData={ocrData}
                    setOcrData={setOcrData}
                    disputeId={disputeId}
                    profile={profile}
                  />
                </Grid>
                <Grid item>
                  <BundleIndex
                    workspaceId={workspaceId}
                    firstLevelFolderId={firstLevelFolderId}
                    reload={handleFullReload}
                    paginationStatus={paginationStatus}
                    setPaginationStatus={setPaginationStatus}
                  />
                </Grid>
              </>
            )}
          </>
        ) : (
          <>
            <Typography style={{ fontSize: "1.2rem", color: "black" }}>
              <IconButton onClick={unSelectAll}>
                <Close />
              </IconButton>
              {selectedFiles.length + selectedFolders.length} Selected
            </Typography>
            <Grid item xs></Grid>
            {!isHidePermissionsFeature && !readOnly && (
              <Grid item>
                <Button
                  color="primary"
                  style={{ background: "white" }}
                  onClick={handleOpenEdit}
                  disabled={isFolderEmpty}
                >
                  Edit Permissions
                </Button>
              </Grid>
            )}
            <Grid item>
              <Button
                color="primary"
                style={{ background: "white" }}
                onClick={handleDownloadMultipleFiles}
                disabled={isFolderEmpty}
              >
                Download
              </Button>
            </Grid>
          </>
        )}
      </Grid>
      <Popover
        id="upload-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        style={{ padding: 0 }}
      >
        <Box className={classes.menuwidth}>
          <Box>
            <Typography
              className={classes.filesandfolder}
              onClick={handleOpenUploadFileModal}
            >
              Files
            </Typography>
            <Typography
              className={classes.filesandfolder}
              onClick={handleOpenUploadFolderModal}
            >
              Folder
            </Typography>
          </Box>
        </Box>
      </Popover>
      <Menu
        id="new-folder"
        anchorEl={anchorElNew}
        keepMounted
        open={Boolean(anchorElNew)}
        onClose={handleCloseNew}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        style={{ padding: 0 }}
      >
        <Box className={classes.menuwidth}>
          <Box>
            <Typography
              className={classes.filesandfolder}
              onClick={createNewFolder}
            >
              Folder
            </Typography>
          </Box>
        </Box>
      </Menu>
      <Box height="3rem" />
      <Grid container>
        {/* Below its minimum width (a phone) the table scrolls sideways
            rather than squeezing its columns. */}
        <TableContainer className={classes.filesTable}>
          <Table
            aria-label="Folders and files"
            style={isCaseManager ? { minWidth: 820 } : undefined}
          >
            <colgroup>
              <col />
              <col style={{ width: 180 }} />
              {isCaseManager && <col style={{ width: 170 }} />}
              <col style={{ width: 200 }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      color="primary"
                      style={{ padding: "5px" }}
                      checked={isAllSelected}
                      indeterminate={!isAllSelected && isAnySelected}
                      onChange={handleSelectAllChange}
                      disabled={totalCount === 0}
                    />
                    <Typography className={classes.tableHeaderContent}>
                      Name
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <Typography className={classes.tableHeaderContent}>
                    Uploaded by
                  </Typography>
                </TableCell>
                {isCaseManager && (
                  <TableCell>
                    <Typography className={classes.tableHeaderContent}>
                      Modified on
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  <Typography className={classes.tableHeaderContent}>
                    Uploaded on
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* folders and files */}
              {showNewFolderInput && !readOnly && (
                <TableRow className={classes.filesHover}>
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        color="primary"
                        style={{ padding: "5px", visibility: "hidden" }}
                      />
                      <Typography className={classes.tableRowContent}>
                        <span style={{ verticalAlign: "middle" }}>
                          <img src={FolderImage} alt="folder" />
                        </span>
                        <span style={{ marginLeft: "1rem" }}>
                          <input
                            ref={inputRef}
                            value={folderName}
                            style={{ width: "70%" }}
                            onChange={handleFolderNameChange}
                            onBlur={handleOnBlur}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleOnBlur();
                              }
                            }}
                          />
                        </span>
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.tableRowContent}>
                      {profile.user.first_name} {profile.user.last_name}
                    </Typography>
                  </TableCell>
                  {isCaseManager && <TableCell />}
                  <TableCell>
                    <Typography className={classes.tableRowContent}>
                      {safeDateFormat(new Date(), "dd/MM/yyyy, HH:mm")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {allFolders.map((folder) => {
                return (
                  <FolderView
                    folder={folder}
                    key={folder.id}
                    disputeId={disputeId}
                    classes={classes}
                    buttonsEnabled={buttonsEnabled}
                    reload={handleFullReload}
                    profile={profile}
                    allProfiles={allProfiles}
                    parent_folder={parent_folder}
                    selectedFolders={selectedFolders}
                    handleChangeOnFolderCheckbox={handleChangeOnFolderCheckbox}
                    unSelectAll={unSelectAll}
                    isHidePermissionsFeature={isHidePermissionsFeature}
                    isFirstLevelFolderOwner={isFirstLevelFolderOwner}
                    customDisputeId={customDisputeId}
                    readOnly={readOnly}
                  />
                );
              })}
              {allFiles.map((file) => {
                return (
                  <FileView
                    file={file}
                    key={file.id}
                    disputeId={disputeId}
                    classes={classes}
                    buttonsEnabled={buttonsEnabled}
                    reload={handleFullReload}
                    profile={profile}
                    allProfiles={allProfiles}
                    parent_folder={parent_folder}
                    selectedFiles={selectedFiles}
                    handleChangeOnFileCheckbox={handleChangeOnFileCheckbox}
                    unSelectAll={unSelectAll}
                    isHidePermissionsFeature={isHidePermissionsFeature}
                    customDisputeId={customDisputeId}
                    readOnly={readOnly}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Grid item xs={12} className={classes.paginationContainer}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => onPageChange && onPageChange(value)}
              color="primary"
            />
          </Grid>
        )}
      </Grid>
      <Box height="4rem" />

      {/* folder and files atop */}
      {openmodalUploadFolder && !readOnly && (
        <UploadFolder
          openmodal={openmodalUploadFolder}
          handleCloseModal={handleCloseUploadFolderModal}
          disputeId={disputeId}
          folder_id={folder_id}
          allProfiles={allProfiles}
          handleCloseModalRefresh={handleCloseUploadFolderModalAndRefresh}
          parent_folder={parent_folder}
          profile={profile}
          isHidePermissionsFeature={isHidePermissionsFeature}
          customDisputeId={customDisputeId}
        />
      )}
      {openmodalUploadFile && !readOnly && (
        <UploadFile
          openmodal={openmodalUploadFile}
          handleCloseModal={handleCloseUploadFileModal}
          disputeId={disputeId}
          folder_id={folder_id}
          allProfiles={allProfiles}
          handleCloseModalRefresh={handleCloseUploadFileModalAndRefresh}
          parent_folder={parent_folder}
          profile={profile}
          isHidePermissionsFeature={isHidePermissionsFeature}
          customDisputeId={customDisputeId}
        />
      )}
      {openmodal && !readOnly && (
        <EditPermissions
          openmodal={openmodal}
          handleCloseModal={handleCloseModal}
          handleCloseModalRefresh={handleCloseModalAndRefresh}
          files={filteredFiles}
          folders={filteredFolders}
          allProfiles={allProfiles}
          parent_folder={parent_folder}
          disputeId={disputeId}
          profile={profile}
          viewPermission={[]}
          editPermission={[]}
        />
      )}
    </Box>
  );
}
